import type { ChangeEvent, ComponentProps, ReactNode } from 'react';
import { useCallback } from 'react';
import { FormField, InputGroup, Paragraph, TextInput } from '@george-labs.com/design-system';

import { useField } from '@luna/form2';

import type { FormFieldProps } from './types/FormFieldProps';
import { shouldShowError } from './FieldErrorVisibilityService';

interface Props extends FormFieldProps {
    autoFocus?: boolean;
    prepend?: ReactNode;
    append?: ReactNode;
    inputProps?: Partial<ComponentProps<typeof TextInput>>;
    isErrorVisible?: boolean;
    shouldClearOnUnmount?: boolean;
}

const LABEL_COLUMN_WIDTH = 4;

export const FormNumberField = ({
    label,
    placeholder,
    'data-cy': dataCy,
    labelColumnWidth = LABEL_COLUMN_WIDTH,
    labelPosition,
    fieldName,
    required,
    disabled,
    warning,
    info,
    alert,
    autoFocus,
    append,
    prepend,
    description,
    inputProps,
    isErrorVisible = true,
    addon,
    shouldClearOnUnmount,
    showErrorBeforeBlur,
}: Props) => {
    const [fieldProps, { error, touched, dirty }, { setValue, setTouched }] = useField<number>(
        fieldName,
        {
            shouldClearOnUnmount,
        },
    );

    const isFieldErrorVisible = shouldShowError(
        isErrorVisible,
        touched,
        error,
        showErrorBeforeBlur,
        dirty,
    );

    const handleChange = useCallback(
        (event: ChangeEvent<HTMLInputElement>) => {
            const { valueAsNumber } = event.target;
            setValue(Number.isNaN(valueAsNumber) ? null : valueAsNumber);
            setTouched();
            inputProps?.onChange?.(event);
        },
        [inputProps, setTouched, setValue],
    );

    return (
        <FormField
            description={description}
            required={required}
            input={
                <InputGroup disabled={disabled} append={append} prepend={prepend}>
                    <TextInput
                        autoFocus={autoFocus}
                        {...fieldProps}
                        {...inputProps}
                        value={fieldProps.value?.toString() || ''}
                        placeholder={placeholder || ''}
                        type={TextInput.TYPE.NUMBER}
                        onChange={handleChange}
                        data-cy={dataCy}
                    />
                </InputGroup>
            }
            label={label}
            placeholder={placeholder}
            error={
                isFieldErrorVisible && <Paragraph data-cy={`${dataCy}-error`}>{error}</Paragraph>
            }
            labelColumnWidth={labelColumnWidth}
            labelPosition={labelPosition}
            disabled={disabled}
            warning={warning}
            info={info}
            alert={alert}
            addon={addon}
        />
    );
};

FormNumberField.LABEL_POSITION = FormField.LABEL_POSITION;
